import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../../../elements/Modal";
import ConsolDropdown from "../../../../elements/ConsolDropdown";
import InputGroup from "../../../../elements/InputGroup";
import { Button } from "../../../../elements/Button";
import buttoncss from "../../../../css/button.module.css";
import commoncss from "../../../../css/common.module.css";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import {
  getControlDateFSGroupingApiHandler,
  getConsolAdjustmentOptionsApiHandler,
} from "../../../../../WorkSpace/Components/ApiServices/apiHandler";

const CONFIG_LABELS = [
  "Investment",
  "Equity share capital",
  "Non-controlling interests",
  "Goodwill",
  "Gain on bargain purchase",
  "Other inputs",
  "Share of profit",
];

const normalizeString = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/[-\u2012\u2013\u2014\u2015]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

const getNodeLabel = (node) => {
  if (!node) return "";
  if (typeof node === "string") return node;
  return (
    node.levelOne ||
    node.levelone ||
    node.levelTwo ||
    node.levelThree ||
    node.name ||
    node.particular ||
    node.section ||
    node.subSection ||
    node.label ||
    ""
  );
};

const findNodeObject = (node, candidates = []) => {
  if (!node) return null;
  if (typeof node === "object") return node;

  const normTarget = normalizeString(node);
  return (
    candidates.find(
      (c) => normalizeString(getNodeLabel(c)) === normTarget
    ) || null
  );
};

const getNodeChildren = (node, parentNode = null, rootOptions = []) => {
  if (!node) return [];

  let resolvedNode = node;
  if (typeof resolvedNode === "string") {
    if (parentNode) {
      const parentChildren = getNodeChildren(parentNode, null, rootOptions);
      resolvedNode = findNodeObject(node, parentChildren);
    }
    if (!resolvedNode || typeof resolvedNode === "string") {
      resolvedNode = findNodeObject(node, rootOptions);
    }
  }

  if (!resolvedNode || typeof resolvedNode === "string") return [];

  return (
    resolvedNode.subgroup ||
    resolvedNode.subGroup ||
    resolvedNode.subSections ||
    resolvedNode.rows ||
    resolvedNode.children ||
    (Array.isArray(resolvedNode.levelTwo) ? resolvedNode.levelTwo : null) ||
    (Array.isArray(resolvedNode.levelThree) ? resolvedNode.levelThree : null) ||
    []
  );
};

const parseGroupingResponse = (res) => {
  if (!res) return [];
  const raw = res?.data !== undefined ? res.data : res;
  if (!raw) return [];

  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.tree)) return raw.tree;
  if (Array.isArray(raw.data?.tree)) return raw.data.tree;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.options)) return raw.options;
  if (Array.isArray(raw.grouping)) return raw.grouping;

  if (typeof raw === "object") {
    const bs = raw.bs || raw.data?.bs || raw.tree?.bs || [];
    const pl = raw.pl || raw.data?.pl || raw.tree?.pl || [];
    if (bs.length > 0 || pl.length > 0) return [...bs, ...pl];
  }

  return [];
};

const ConsolEntryConfigModal = ({
  isOpen,
  onClose,
  fsType = "standalone",
  fsId,
  consolID,
  mappingValue = [],
  onSave,
}) => {
  const [optionsData, setOptionsData] = useState([]);
  const [mappings, setMappings] = useState(() => {
    const initial = {};
    CONFIG_LABELS.forEach((lbl) => {
      initial[lbl] = { levelOne: null, levelTwo: null, levelThree: null };
    });
    return initial;
  });

  // Fetch FS Grouping Options via getControlDateFSGrouping
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const loadOptions = async () => {
      let fetchedOptions = [];

      // Determine fsId from props
      const targetFsId = fsType === "consol" ? (consolID || fsId) : (fsId || consolID);
      const targetFsType = fsType || "standalone";

      if (targetFsId) {
        try {
          console.log("[ConsolEntryConfigModal] Calling getControlDateFSGrouping:", { fsType: targetFsType, fsId: targetFsId });
          const res = await getControlDateFSGroupingApiHandler(targetFsType, targetFsId);
          console.log("[ConsolEntryConfigModal] getControlDateFSGrouping response:", res?.data);
          fetchedOptions = parseGroupingResponse(res);
        } catch (err) {
          console.error("[ConsolEntryConfigModal] Failed to fetch controlDateFSGrouping:", err);
        }
      }

      // Optional fallback: getConsolAdjustmentOptionsApiHandler ONLY if consolID is present
      if (fetchedOptions.length === 0 && consolID) {
        try {
          console.log("[ConsolEntryConfigModal] Fallback - Fetching adjustmentOptions for consolID:", consolID);
          const res = await getConsolAdjustmentOptionsApiHandler({ consolID });
          fetchedOptions = parseGroupingResponse(res);
        } catch (err) {
          console.error("[ConsolEntryConfigModal] Failed fallback adjustmentOptions:", err);
        }
      }

      if (isMounted) {
        console.log("[ConsolEntryConfigModal] Total options loaded:", fetchedOptions.length);
        setOptionsData(fetchedOptions);
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
  }, [isOpen, fsType, fsId, consolID]);

  // Populate mapping state from mappingValue prop
  useEffect(() => {
    if (!isOpen) return;

    const initial = {};
    CONFIG_LABELS.forEach((lbl) => {
      initial[lbl] = { levelOne: null, levelTwo: null, levelThree: null };
    });

    if (Array.isArray(mappingValue) && mappingValue.length > 0) {
      mappingValue.forEach((m) => {
        const itemKey = m.item || m.label || m.name;
        const normKey = normalizeString(itemKey);
        const matchLabel = CONFIG_LABELS.find(
          (lbl) => normalizeString(lbl) === normKey
        );

        if (matchLabel) {
          const normLvl1 = normalizeString(m.levelOne);
          const lvl1 =
            optionsData.find(
              (o) => normalizeString(getNodeLabel(o)) === normLvl1
            ) ||
            m.levelOne ||
            null;

          const normLvl2 = normalizeString(m.levelTwo);
          const lvl1Children = getNodeChildren(lvl1, null, optionsData);
          const lvl2 =
            lvl1Children.find(
              (s) => normalizeString(getNodeLabel(s)) === normLvl2
            ) ||
            m.levelTwo ||
            null;

          const normLvl3 = normalizeString(m.levelThree);
          const lvl2Children = getNodeChildren(lvl2, lvl1, optionsData);
          const lvl3 =
            lvl2Children.find(
              (s) => normalizeString(getNodeLabel(s)) === normLvl3
            ) ||
            m.levelThree ||
            null;

          initial[matchLabel] = {
            levelOne: lvl1,
            levelTwo: lvl2,
            levelThree: lvl3,
          };
        }
      });
    }

    setMappings(initial);
  }, [isOpen, mappingValue, optionsData]);

  const level1Options = useMemo(
    () =>
      optionsData.map((item) => ({
        label: getNodeLabel(item),
        value: item,
      })),
    [optionsData]
  );

  const handleMappingChange = (lbl, field, value) => {
    setMappings((prev) => {
      const current = prev[lbl] || { levelOne: null, levelTwo: null, levelThree: null };
      if (field === "levelOne") {
        return {
          ...prev,
          [lbl]: { levelOne: value, levelTwo: null, levelThree: null },
        };
      }
      if (field === "levelTwo") {
        return {
          ...prev,
          [lbl]: { ...current, levelTwo: value, levelThree: null },
        };
      }
      if (field === "levelThree") {
        return {
          ...prev,
          [lbl]: { ...current, levelThree: value },
        };
      }
      return prev;
    });
  };

  const handleSave = () => {
    const autoAdjustmentMapping = CONFIG_LABELS.map((lbl) => {
      const m = mappings[lbl] || {};
      return {
        item: lbl,
        label: lbl,
        levelOne: getNodeLabel(m.levelOne),
        levelTwo: getNodeLabel(m.levelTwo),
        levelThree: getNodeLabel(m.levelThree),
      };
    }).filter((m) => m.levelOne || m.levelTwo || m.levelThree);

    if (onSave) {
      onSave(autoAdjustmentMapping);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      width="780px"
      title="Configure Mapping"
      bodycss={commoncss.unsetPadding}
      headerActions={<CloseIcon className="clickable" onClick={onClose} />}
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            label="Cancel"
            onClick={onClose}
          />
          <Button
            btnColor={buttoncss.primary}
            label="Save Mapping"
            onClick={handleSave}
          />
        </>
      }
    >
      <div className={commoncss.pd1530} style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {CONFIG_LABELS.map((lbl, idx) => {
          const mapState = mappings[lbl] || {};
          const lvl1Children = getNodeChildren(mapState.levelOne, null, optionsData);
          const lvl2Children = getNodeChildren(mapState.levelTwo, mapState.levelOne, optionsData);

          return (
            <div
              key={lbl}
              style={{
                border: "1px solid #EAEEED",
                borderRadius: "8px",
                padding: "14px",
                marginBottom: "14px",
                position: "relative",
                zIndex: CONFIG_LABELS.length - idx,
              }}
            >
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "14px",
                  color: "#111111",
                  marginBottom: "10px",
                }}
              >
                {lbl}
              </div>

              <div className="row">
                {/* Level 1 */}
                <div className="col-md-4" style={{ paddingRight: "5px" }}>
                  <InputGroup
                    label="Level 1"
                    inputs={[
                      <ConsolDropdown
                        options={level1Options}
                        selected={
                          mapState.levelOne
                            ? {
                                label: getNodeLabel(mapState.levelOne),
                                value: mapState.levelOne,
                              }
                            : null
                        }
                        onSelect={(sel) =>
                          handleMappingChange(lbl, "levelOne", sel ? sel.value : null)
                        }
                        placeHolder="Select Level 1"
                        className={commoncss.placeholderStyle}
                        isEditable={true}
                      />,
                    ]}
                  />
                </div>

                {/* Level 2 */}
                <div className="col-md-4" style={{ paddingLeft: "5px", paddingRight: "5px" }}>
                  <InputGroup
                    label="Level 2"
                    inputs={[
                      <ConsolDropdown
                        options={lvl1Children.map((s) => ({
                          label: getNodeLabel(s),
                          value: s,
                        }))}
                        selected={
                          mapState.levelTwo
                            ? {
                                label: getNodeLabel(mapState.levelTwo),
                                value: mapState.levelTwo,
                              }
                            : null
                        }
                        onSelect={(sel) =>
                          handleMappingChange(lbl, "levelTwo", sel ? sel.value : null)
                        }
                        disabled={!mapState.levelOne}
                        placeHolder="Select Level 2"
                        className={commoncss.placeholderStyle}
                        isEditable={true}
                      />,
                    ]}
                  />
                </div>

                {/* Level 3 */}
                <div className="col-md-4" style={{ paddingLeft: "5px" }}>
                  <InputGroup
                    label="Level 3"
                    inputs={[
                      <ConsolDropdown
                        options={lvl2Children.map((s) => ({
                          label: getNodeLabel(s),
                          value: s,
                        }))}
                        selected={
                          mapState.levelThree
                            ? {
                                label: getNodeLabel(mapState.levelThree),
                                value: mapState.levelThree,
                              }
                            : null
                        }
                        onSelect={(sel) =>
                          handleMappingChange(lbl, "levelThree", sel ? sel.value : null)
                        }
                        disabled={!mapState.levelTwo}
                        placeHolder="Select Level 3"
                        className={commoncss.placeholderStyle}
                        isEditable={true}
                      />,
                    ]}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ConsolEntryConfigModal;
